import * as React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import { normalizeKey } from '~shared/utils/normalize-keycode';
import warning from '~shared/utils/warning';

import {
  Action,
  TAB_BUTTON_CLICK,
  TAB_BUTTON_END,
  TAB_BUTTON_ENTER,
  TAB_BUTTON_HOME,
  TAB_BUTTON_NEXT,
  TAB_BUTTON_PREV,
  TAB_BUTTON_SPACE,
  TAB_BUTTON_TAB,
  TAB_LIST_FOCUSED,
} from './actions';
import { TabListContext } from './TabList';
import { TabsContext } from './TabsProvider';
import { TabsOrientation } from '.';

interface TabRenderProps
  extends Pick<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'onKeyDown' | 'onKeyUp' | 'onFocus' | 'onClick'
  > {
  isActive: boolean;
  ref: React.MutableRefObject<HTMLButtonElement>;
  role: string;
  tabIndex: undefined | number;
  'aria-selected': boolean;
  'aria-controls': string;
  id: string;
  disabled: boolean;
  orientation: TabsOrientation;
}

export interface TabProps {
  children: (props: TabRenderProps) => React.ReactElement;
  item?: string;
  isDisabled?: boolean;
}

/**
 * @deprecated Use the TabsV2 components instead.
 */
export function Tab(props: TabProps) {
  const { children, item, isDisabled = false } = props;

  const tabsContext = React.useContext(TabsContext);
  const tabListContext = React.useContext(TabListContext);
  const tabRef = React.useRef<HTMLButtonElement>(null);

  warning(
    tabListContext !== null,
    'A Tab must be rendered as a child of TabList.'
  );

  if (!tabListContext) {
    throw new Error('Tab rendered outside TabList context');
  }

  if (!tabsContext) {
    throw new Error('Tab rendered outside TabsProvider');
  }

  const { focusedTabIndex, selectedIndex, dispatch, a11y, onChange } =
    tabsContext;
  const { tabListItemIndex /*, scrollContainer*/, orientation } =
    tabListContext;
  const isSelected = selectedIndex === tabListItemIndex;
  const isFocused = focusedTabIndex === tabListItemIndex;

  React.useEffect(() => {
    if (tabRef.current && isFocused) {
      tabRef.current.focus();
    }
  }, [isFocused]);

  const handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const targetKey = normalizeKey(event);

    if (targetKey === 'Tab') return;

    if (onChange && (targetKey === 'Enter' || targetKey === ' ')) {
      onChange(tabListItemIndex, item);
    }

    const action: Record<string, Action> = {
      ArrowLeft:
        orientation === 'horizontal' ? { type: TAB_BUTTON_PREV } : null,
      ArrowRight:
        orientation === 'horizontal' ? { type: TAB_BUTTON_NEXT } : null,
      ArrowUp: orientation === 'vertical' ? { type: TAB_BUTTON_PREV } : null,
      ArrowDown: orientation === 'vertical' ? { type: TAB_BUTTON_NEXT } : null,
      Home: { type: TAB_BUTTON_HOME },
      End: { type: TAB_BUTTON_END },
      Enter: { type: TAB_BUTTON_ENTER, value: tabListItemIndex },
      ' ': { type: TAB_BUTTON_SPACE, value: tabListItemIndex },
    };

    if (action[targetKey]) {
      dispatch(action[targetKey]);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const targetKey = normalizeKey(event);

    if (targetKey === 'Tab' && focusedTabIndex !== null) {
      dispatch({ type: TAB_BUTTON_TAB });
      return;
    }

    const isArrowPress = () => {
      if (orientation === 'horizontal') {
        return (
          targetKey.indexOf('ArrowRight') === 0 ||
          targetKey.indexOf('ArrowLeft') === 0
        );
      }
      if (orientation === 'vertical') {
        return (
          targetKey.indexOf('ArrowUp') === 0 ||
          targetKey.indexOf('ArrowDown') === 0
        );
      }
    };
    const isActionKeyPress = targetKey === 'Enter' || targetKey === ' ';

    /**
     * Prevent arrow keys scrolling the document while navigating the tabs and
     * prevent enter or space press from triggering the click handler.
     */
    if (isArrowPress || isActionKeyPress) {
      event.preventDefault();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (onChange) {
      onChange(tabListItemIndex, item);
    }

    dispatch({ type: TAB_BUTTON_CLICK, value: tabListItemIndex });
  };

  const handleFocus = () =>
    dispatch({
      type: TAB_LIST_FOCUSED,
      value: tabListItemIndex,
    });

  const itemProps: TabRenderProps = {
    isActive: isSelected,
    type: 'button',
    ref: tabRef,
    ...a11y.tabProps({ tabIndex: tabListItemIndex, isSelected }),
    disabled: isDisabled,
    orientation: orientation,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    onFocus: isSelected ? handleFocus : undefined,
    onClick: handleClick,
  };

  return children(itemProps);
}

type StyledProps = Omit<TabProps, 'children'> & {
  children: React.ReactNode;
};

export function StyledTab({ children, ...restProps }: StyledProps) {
  const getTabClass = (isDisabled: boolean) =>
    cx(
      'group relative flex justify-center items-center m-1 px-4 py-2',
      'font-sans text-base font-600 leading-8',
      'bg-transparent border-0 rounded-1 appearance-none outline-none select-none',
      isDisabled ? 'text-grey-600' : 'text-blue-900 cursor-pointer'
    );
  const getIndicatorClass = (
    isActive: boolean,
    isDisabled: boolean,
    orientation: TabsOrientation
  ) =>
    cx(
      'absolute right-0 bottom-0 rounded-1 pointer-events-none',
      orientation === 'horizontal' && 'left-0 h-1 mx-4',
      orientation === 'vertical' && 'top-0 w-1 my-2',
      !isDisabled && [isActive ? 'bg-cyan-700' : 'group-hocus:bg-grey-600']
    );

  return (
    <Tab {...restProps}>
      {({ isActive, orientation, ...props }) => (
        <button {...props} className={getTabClass(props.disabled)}>
          <FocusIndicator notOffset />
          <div
            className={getIndicatorClass(isActive, props.disabled, orientation)}
            style={{ maxHeight: '2px' }}
          />
          {children}
        </button>
      )}
    </Tab>
  );
}
